export type OrganisationRole = "owner" | "admin" | "coach" | "member" | "medical" | "athlete";

export type OrganisationMemberStatus = "active" | "pending" | "suspended";

export interface OrganisationMemberPrivacySettings {
    wellnessLogs: boolean;
    femaleCycleLogs: boolean;
    trainingLogs: boolean;
    niggleLogs: boolean;
    performanceLogs: boolean;
    healthLogs: boolean;
    noteLogs: boolean;
    recoveryLogs: boolean;
    updatedAt?: Date;
}

export interface OrganisationMember {
    userId: string;
    orgId: string;
    role: OrganisationRole;
    joinedAt?: Date;
    invitedBy?: string;
    status: OrganisationMemberStatus;
    team?: string | null;
    teams?: string[];
    groups?: string[];
    profile?: unknown;
    privacySettings?: OrganisationMemberPrivacySettings;
}

export interface Organisation {
    id: string;
    name: string;
    createdAt?: Date;
    updatedAt?: Date;
    lastUpdated?: Date;
    ownerId?: string;
    inviteCode?: string;
    onboardingComplete?: boolean;
    adminActivityCatIDs?: string[];
    logoUrl?: string;
    imageUrl?: string;
    description?: string;
    teams?: string[];
    groups?: Record<string, string[]>;
    members: OrganisationMember[];
}

// Owner access now comes only from an active owners/{uid} doc, not from the
// organisation's ownerId field.
export type OwnerDocIssue = "missing" | "inactive";

export interface OrganisationWithCounts extends Organisation {
    ownerCount: number;
    coachCount: number;
    athleteCount: number;
    config?: OrganisationConfig;
    ownerDocIssue: OwnerDocIssue | null;
}

export type OrganisationSubcollectionName = "owners" | "coaches" | "medicals" | "members";

export interface OrganisationSubcollectionEntry {
    id: string;
    userId?: string;
    role?: OrganisationRole | string;
    status?: OrganisationMemberStatus | string;
    joinedAt?: Date;
    teams?: string[] | null;
    team?: string | null;
    [key: string]: unknown;
}

export interface OrganisationMemberNote {
    id: string;
    memberId: string;
    content: string;
    createdBy?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export type OrganisationBillingPeriod = "quarterly" | "annually";

// NOTE: "team" (singular) — must match the value written by PeakPWA/functions.
export type OrganisationType = "individual" | "team" | "commercial";

/**
 * Plans a commercial organisation can grant its athletes.
 * "default" is Basic: no subscription through the organisation.
 * Add "platinum" here when that plan should be offered.
 */
export const ATHLETE_SUBSCRIPTION_OPTIONS = ["premium", "plus", "default"] as const;
export type AthleteSubscription = (typeof ATHLETE_SUBSCRIPTION_OPTIONS)[number];

export const ATHLETE_SUBSCRIPTION_LABELS: Record<AthleteSubscription, string> = {
    premium: "Premium",
    plus: "Plus",
    default: "Basic",
};

/** Missing or unrecognised values (including platinum, which is not offered yet) stay Premium. */
export const resolveAthleteSubscription = (value: unknown): AthleteSubscription =>
    value === "plus" || value === "default" ? value : "premium";

// Mirrors OrganisationConfig in PeakPWA/functions/src/subscriptionService.ts and peakElite.ts.
export interface OrganisationConfig {
    maxAthletes: number;
    maxAthletesForPas: number;
    orgType: OrganisationType;
    maxCoaches: number;
    maxTeams: number;
    billingPeriod?: OrganisationBillingPeriod;
    billingPeriodStart?: Date;
    billingPeriodEnd?: Date;
    onboardingRequested: boolean;
    status?: "trial" | "active" | "pastDue" | "paused" | "canceled";
    /**
     * Plan athletes receive through a commercial organisation.
     * Only applied when orgType is "commercial". Individual and team orgs always grant Premium.
     */
    athleteSubscription: AthleteSubscription;
}

export const DEFAULT_ORGANISATION_CONFIG: OrganisationConfig = {
    maxAthletes: 0,
    maxAthletesForPas: 0,
    orgType: "individual",
    maxCoaches: 0,
    maxTeams: 0,
    billingPeriod: "annually",
    onboardingRequested: false,
    athleteSubscription: "premium",
};
